import { ComponentProps, Fragment } from 'react';
import {
  useColorModeValue,
  Text,
  HStack,
  Grid,
  GridItem,
  Box,
  Button,
  IconButton,
  Popover,
  PopoverTrigger,
  Skeleton,
  Menu,
  Tooltip,
  MenuButton,
  Portal,
  MenuList,
  MenuItem,
  Divider,
  MenuOptionGroup,
  MenuItemOption,
  Fade,
  Spinner,
} from '@chakra-ui/react';
import {
  faColumns,
  faCompress,
  faEllipsisH,
  faExpand,
  faFileExport,
  faFilter,
  faSitemap,
  faArrowsDownToLine,
  faTable,
  faChartColumn,
  faChartBar,
  faChartLine,
  faChartPie,
  faAngleDown,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useFlag } from '@unleash/proxy-client-react';
import { useMutation } from 'urql';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AggregatedOrderViewType,
  EventResource,
  EventType,
  OrderDeepFragment,
  ProjectDeepFragment,
  UpdateOrderDocument,
} from '../../../gql/graphql';
import {
  Panel,
  PanelActionButtons,
  PanelContent,
  PanelHeader,
  PanelTitle,
} from '../../common/Panel';
import SparkelIcon from '../../common/icon/SparkelIcon';
import { usePanelFullscreen } from '../../common/PanelFullscreenContext';
import { ReactComponent as IsolationIcon } from '../../../assets/icons/isolation.svg';
import AggregatedOrderTable from './aggregated-order-table';
import AggregatedOrderChart from './AggregatedOrderChart';
import { FilterPopover } from './FilterPopover';
import { useAggregatedOrderPanel } from './useAggregatedOrderPanel';
import { GroupPopOver } from './group-popover';
import { useEventHistory } from 'src/hooks/event-history';
import { useUserTenant } from 'src/services/auth-info';

type AggregatedOrderPanelProps = {
  thisOrder: OrderDeepFragment;
  project: ProjectDeepFragment;
  isReadOnly?: boolean;
} & ComponentProps<typeof Panel>;

// eslint-disable-next-line complexity
const AggregatedOrderPanel = ({
  thisOrder: order,
  project,
  isReadOnly,
  ...props
}: AggregatedOrderPanelProps) => {
  const {
    isLoading,
    resolvedOrders,
    isIsolationToggleActive,
    resizeableAreaRef,
    selectedOrderEntries,
    isFilterPopoverOpen,
    filterPopoverRef,
    panelRef,
    table,
    showColumnManagementModal,
    itemGroups,
    isGroupPopoverOpen,
    groupPopoverRef,
    grouping,
    shouldFlatten,
    isFlatteningLoading,
    toggleFlattenedStatus,
    setGrouping,
    onGroupPopoverToggle,
    onGroupPopoverClose,
    onColumnManagementModalClose,
    onColumnManagementModalOpen,
    setRowSelection,
    onFilterPopoverClose,
    setIsUsingCascader,
    onFilterPopoverOpen,
    handleExportToXlsx,
    handleOnFilterClear,
    handleOnFiltersChange,
    handleMouseDown,
    setIsolationToggleActive,
    handleColumnManagementSubmit,
    renamingColumnId,
    renameColumn,
  } = useAggregatedOrderPanel(order, project);

  const { isFullscreen, toggleFullscreen } = usePanelFullscreen();

  const contextFontColor = useColorModeValue('gray.400', 'gray.500');
  const contextBackgroundColor = useColorModeValue('gray.50', 'gray.900');
  const activeIconColor = useColorModeValue('teal.500', 'teal.200');
  const iconButtonBackgroundColor = useColorModeValue('teal.50', undefined);

  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useTranslation(['table', 'common']);

  const { isFreeContractor } = useUserTenant();

  const enableChartView = useFlag('enable-chart-view');

  const isFiltering =
    !!order.aggregatedOrderFilters && order.aggregatedOrderFilters.length > 0;

  const isGrouping = grouping.length === 0;

  // Combined view/chart type state
  const viewType = order.aggregatedOrderView || AggregatedOrderViewType.Table;

  // Derive isChartView from viewType
  const isChartView = viewType !== AggregatedOrderViewType.Table;

  const [, updateOrder] = useMutation(UpdateOrderDocument);

  const { logEvent } = useEventHistory(order.projectId);

  const handleViewTypeChange = async (newViewType: AggregatedOrderViewType) => {
    const result = await updateOrder({
      input: {
        id: order.id,
        patch: {
          aggregatedOrderView: newViewType,
        },
      },
    });

    if (result.error) {
      console.error(result.error);
    } else {
      logEvent({
        eventName: 'Table View Type Changed',
        eventType: EventType.Modified,
        resourceName: order.name,
        resourceType: EventResource.Order,
        metadata: {
          affectedResources: [order.id],
          details: `Changed table ${order.name} view type`,
        },
      });
    }
  };

  return (
    <GridItem
      zIndex={isFullscreen ? 20 : 9}
      ref={resizeableAreaRef}
      area={isFullscreen ? undefined : 'main'}
      gridColumn={isFullscreen ? 2 : undefined}
      gridRow={isFullscreen ? '1 / -1' : undefined}
      justifySelf="center"
      maxWidth="100%"
      maxHeight="100%"
      marginBottom={isFullscreen ? 0 : 2}
      overflow="auto"
      alignSelf={isFullscreen ? 'stretch' : 'end'}
      width={isFullscreen ? '100%' : undefined}
      height={isFullscreen ? '100%' : undefined}
      paddingY={isFullscreen ? 2 : undefined}
      sx={{
        '@media (max-width: 1200px)': {
          paddingRight: 2,
        },
      }}
    >
      <Panel
        display="flex"
        flexDirection="column"
        height={isFullscreen ? '100%' : undefined}
        {...props}
        onMouseDown={() => null}
      >
        {!isFullscreen && (
          <Box
            onMouseDown={handleMouseDown}
            style={{ touchAction: 'none' }}
            cursor="ns-resize"
            height="2"
            width="100%"
            alignSelf="center"
            _active={{ backgroundColor: 'gray.500' }}
            opacity={0.4}
            marginTop="-3"
            marginBottom="2"
            borderRadius="2px"
            userSelect={'none'}
          />
        )}
        <PanelHeader>
          <PanelTitle>{order.name}</PanelTitle>
          <PanelActionButtons>
            {selectedOrderEntries && selectedOrderEntries.length > 0 && (
              <HStack
                backgroundColor={contextBackgroundColor}
                paddingX={2}
                borderRadius={'full'}
                alignContent={'center'}
                minHeight={6}
              >
                <Text color={contextFontColor} fontSize={'xs'}>
                  {selectedOrderEntries.length} item
                  {selectedOrderEntries.length > 1 ? 's' : ''} selected
                </Text>
              </HStack>
            )}
            {!isReadOnly && (
              <>
                <HStack gap={1}>
                  {enableChartView && !isFreeContractor && (
                    <Menu closeOnSelect={true} isLazy>
                      <Tooltip label={t('table:view-chart')}>
                        <MenuButton>
                          <Button
                            borderRadius={'3xl'}
                            colorScheme="gray"
                            variant="solid-no-transparent"
                            size="xs"
                            aria-label="Select view type"
                            rightIcon={<SparkelIcon icon={faAngleDown} />}
                          >
                            <SparkelIcon
                              color={['gray.600', 'gray.50']}
                              icon={getViewTypeIcon(viewType)}
                            />
                          </Button>
                        </MenuButton>
                      </Tooltip>
                      <Portal>
                        <MenuList zIndex={99}>
                          <MenuOptionGroup type="radio" value={viewType}>
                            <MenuItemOption
                              value={AggregatedOrderViewType.Table}
                              onClick={() =>
                                handleViewTypeChange(
                                  AggregatedOrderViewType.Table
                                )
                              }
                            >
                              <HStack justify="space-between" width="100%">
                                <HStack>
                                  <SparkelIcon icon={faTable} fixedWidth />
                                  <Text>{t('table:view-type.table')}</Text>
                                </HStack>
                              </HStack>
                            </MenuItemOption>
                            <Divider />
                            <MenuItemOption
                              value={AggregatedOrderViewType.BarChart}
                              onClick={() =>
                                handleViewTypeChange(
                                  AggregatedOrderViewType.BarChart
                                )
                              }
                            >
                              <HStack justify="space-between" width="100%">
                                <HStack>
                                  <SparkelIcon
                                    icon={faChartColumn}
                                    fixedWidth
                                  />
                                  <Text>{t('table:view-type.bar-chart')}</Text>
                                </HStack>
                              </HStack>
                            </MenuItemOption>
                            <MenuItemOption
                              value={AggregatedOrderViewType.StackedBarChart}
                              onClick={() =>
                                handleViewTypeChange(
                                  AggregatedOrderViewType.StackedBarChart
                                )
                              }
                            >
                              <HStack justify="space-between" width="100%">
                                <HStack>
                                  <SparkelIcon
                                    icon={faChartColumn}
                                    fixedWidth
                                  />
                                  <Text>
                                    {t('table:view-type.stacked-bar-chart')}
                                  </Text>
                                </HStack>
                              </HStack>
                            </MenuItemOption>
                            <Divider />
                            <MenuItemOption
                              value={AggregatedOrderViewType.HorizontalBarChart}
                              onClick={() =>
                                handleViewTypeChange(
                                  AggregatedOrderViewType.HorizontalBarChart
                                )
                              }
                            >
                              <HStack justify="space-between" width="100%">
                                <HStack>
                                  <SparkelIcon icon={faChartBar} fixedWidth />
                                  <Text>
                                    {t('table:view-type.horizontal-bar-chart')}
                                  </Text>
                                </HStack>
                              </HStack>
                            </MenuItemOption>
                            <MenuItemOption
                              value={
                                AggregatedOrderViewType.StackedHorizontalBarChart
                              }
                              onClick={() =>
                                handleViewTypeChange(
                                  AggregatedOrderViewType.StackedHorizontalBarChart
                                )
                              }
                            >
                              <HStack justify="space-between" width="100%">
                                <HStack>
                                  <SparkelIcon icon={faChartBar} fixedWidth />
                                  <Text>
                                    {t(
                                      'table:view-type.stacked-horizontal-bar-chart'
                                    )}
                                  </Text>
                                </HStack>
                              </HStack>
                            </MenuItemOption>
                            <Divider />
                            <MenuItemOption
                              value={AggregatedOrderViewType.LineChart}
                              onClick={() =>
                                handleViewTypeChange(
                                  AggregatedOrderViewType.LineChart
                                )
                              }
                            >
                              <HStack justify="space-between" width="100%">
                                <HStack>
                                  <SparkelIcon icon={faChartLine} fixedWidth />
                                  <Text>{t('table:view-type.line-chart')}</Text>
                                </HStack>
                              </HStack>
                            </MenuItemOption>
                            <Divider />
                            <MenuItemOption
                              value={AggregatedOrderViewType.DoughnutChart}
                              onClick={() =>
                                handleViewTypeChange(
                                  AggregatedOrderViewType.DoughnutChart
                                )
                              }
                            >
                              <HStack justify="space-between" width="100%">
                                <HStack>
                                  <SparkelIcon icon={faChartPie} fixedWidth />
                                  <Text>
                                    {t('table:view-type.doughnut-chart')}
                                  </Text>
                                </HStack>
                              </HStack>
                            </MenuItemOption>
                          </MenuOptionGroup>
                        </MenuList>
                      </Portal>
                    </Menu>
                  )}
                  {!isFreeContractor && (
                    <>
                      <Popover
                        isOpen={isGroupPopoverOpen}
                        onClose={onGroupPopoverClose}
                        closeOnBlur={false}
                        isLazy
                      >
                        <PopoverTrigger>
                          <Button
                            size={'xs'}
                            leftIcon={
                              <SparkelIcon icon={faSitemap} color="inherit" />
                            }
                            colorScheme={!isGrouping ? 'teal' : 'gray'}
                            color={!isGrouping ? activeIconColor : undefined}
                            isActive={!isGrouping}
                            backgroundColor={
                              !isGrouping
                                ? iconButtonBackgroundColor
                                : undefined
                            }
                            variant={!isGrouping ? 'toggle' : undefined}
                            onClick={onGroupPopoverToggle}
                          >
                            {t('table:grouping.button')}
                          </Button>
                        </PopoverTrigger>
                        <Portal>
                          <Box ref={groupPopoverRef}>
                            <GroupPopOver
                              orderId={order.id}
                              projectId={order.projectId}
                              orderColumns={order.orderColumns}
                              columnNameMapping={order.columnNameMapping}
                              {...{
                                grouping,
                                shouldFlatten,
                                setGrouping,
                              }}
                            />
                          </Box>
                        </Portal>
                      </Popover>

                      <Popover
                        isOpen={isFilterPopoverOpen}
                        onClose={onFilterPopoverClose}
                        closeOnBlur={false}
                        isLazy
                      >
                        <PopoverTrigger>
                          <Button
                            size={'xs'}
                            leftIcon={
                              <SparkelIcon icon={faFilter} color={'inherit'} />
                            }
                            colorScheme={isFiltering ? 'teal' : 'gray'}
                            variant={isFiltering ? 'toggle' : undefined}
                            isActive={isFiltering}
                            color={isFiltering ? activeIconColor : undefined}
                            backgroundColor={
                              isFiltering
                                ? iconButtonBackgroundColor
                                : undefined
                            }
                            onClick={
                              isFilterPopoverOpen
                                ? onFilterPopoverClose
                                : onFilterPopoverOpen
                            }
                          >
                            {t('table:filter.button')}
                          </Button>
                        </PopoverTrigger>
                        <Portal>
                          <Box ref={filterPopoverRef}>
                            <FilterPopover
                              resolvedOrders={resolvedOrders || []}
                              itemGroups={itemGroups}
                              orderColumns={order.orderColumns}
                              columnNameMapping={order.columnNameMapping}
                              isOpen={isFilterPopoverOpen}
                              isUpdatingTable={isLoading}
                              onFiltersChange={handleOnFiltersChange}
                              onClearFilter={handleOnFilterClear}
                              onUsingCascader={setIsUsingCascader}
                              defaultFilters={
                                order.aggregatedOrderFilters || []
                              }
                            />
                          </Box>
                        </Portal>
                      </Popover>
                      <Tooltip label={t('flatten.tooltip')}>
                        <IconButton
                          aria-label="Flatten Items"
                          isRound
                          size="xs"
                          colorScheme={shouldFlatten ? 'teal' : 'gray'}
                          variant={shouldFlatten ? 'toggle' : undefined}
                          isActive={shouldFlatten}
                          isLoading={isFlatteningLoading}
                          backgroundColor={
                            shouldFlatten
                              ? iconButtonBackgroundColor
                              : undefined
                          }
                          icon={
                            <SparkelIcon
                              icon={faArrowsDownToLine}
                              color={
                                shouldFlatten ? activeIconColor : undefined
                              }
                            />
                          }
                          onClick={toggleFlattenedStatus}
                        />
                      </Tooltip>
                    </>
                  )}
                </HStack>
              </>
            )}
            <Tooltip label={t('table:focus-mode.tooltip')}>
              <IconButton
                aria-label="Focus mode"
                isRound
                size={'xs'}
                colorScheme={isIsolationToggleActive ? 'teal' : 'gray'}
                variant={isIsolationToggleActive ? 'toggle' : undefined}
                isActive={isIsolationToggleActive}
                backgroundColor={
                  isIsolationToggleActive
                    ? iconButtonBackgroundColor
                    : undefined
                }
                icon={
                  <SparkelIcon
                    as={IsolationIcon}
                    color={
                      isIsolationToggleActive ? activeIconColor : undefined
                    }
                  />
                }
                onClick={() =>
                  setIsolationToggleActive(!isIsolationToggleActive)
                }
              />
            </Tooltip>
            {!isReadOnly && (
              <>
                {/* For free contractors, show Excel export button directly */}
                {isFreeContractor ? (
                  <Tooltip label={t('table:menu-item.export-to-excel')}>
                    <IconButton
                      aria-label="Export to Excel"
                      icon={<SparkelIcon icon={faFileExport} />}
                      size="xs"
                      colorScheme="gray"
                      variant="ghost"
                      onClick={handleExportToXlsx}
                    />
                  </Tooltip>
                ) : (
                  <Menu isLazy>
                    <Tooltip label={t('table:more-actions.tooltip')}>
                      <MenuButton
                        aria-label="More actions"
                        as={IconButton}
                        size={'xs'}
                        maxWidth={16}
                        colorScheme="gray"
                        variant="ghost"
                      >
                        <SparkelIcon icon={faEllipsisH} />
                      </MenuButton>
                    </Tooltip>

                    <Portal>
                      <MenuList zIndex={99}>
                        <MenuItem
                          aria-label="Export to Excel"
                          icon={<SparkelIcon icon={faFileExport} fixedWidth />}
                          onClick={handleExportToXlsx}
                        >
                          {t('table:menu-item.export-to-excel')}
                        </MenuItem>
                        <MenuItem
                          aria-label="Manage columns"
                          icon={<SparkelIcon icon={faColumns} fixedWidth />}
                          onClick={onColumnManagementModalOpen}
                        >
                          {t('table:menu-item.manage-columns')}
                        </MenuItem>
                      </MenuList>
                    </Portal>
                  </Menu>
                )}
              </>
            )}
            <Tooltip
              label={
                isFullscreen
                  ? t('table:exit-fullscreen')
                  : t('table:enter-fullscreen')
              }
            >
              <IconButton
                aria-label={
                  isFullscreen
                    ? t('table:exit-fullscreen')
                    : t('table:enter-fullscreen')
                }
                isRound
                size="xs"
                colorScheme="gray"
                variant="ghost"
                icon={
                  <SparkelIcon icon={isFullscreen ? faCompress : faExpand} />
                }
                onClick={toggleFullscreen}
              />
            </Tooltip>
            {!isFreeContractor && (
              <Tooltip label={t('table:close-panel')}>
                <IconButton
                  aria-label={t('table:close-panel')}
                  isRound
                  size="xs"
                  colorScheme="gray"
                  variant="ghost"
                  icon={<SparkelIcon icon={faTimes} />}
                  onClick={() =>
                    navigate(`/project/${project.id}/takeoff${location.search}`)
                  }
                />
              </Tooltip>
            )}
          </PanelActionButtons>
        </PanelHeader>
        <PanelContent
          ref={panelRef}
          marginTop={-2}
          padding={0}
          position={'relative'}
          display={'flex'}
          height={isFullscreen ? '100%' : 300}
          overflowY={'hidden'}
          flex={isFullscreen ? 1 : undefined}
        >
          {isChartView ? (
            <>
              <AggregatedOrderChart
                table={table}
                grouping={grouping}
                rowSelection={table.getState().rowSelection}
                setRowSelection={setRowSelection}
                viewType={viewType}
              />
              {isLoading && (
                <Fade in={isLoading}>
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    backgroundColor={contextBackgroundColor}
                    opacity={0.5}
                    zIndex={1}
                  >
                    <Spinner size="lg" thickness="4px" speed="0.65s" />
                  </Box>
                </Fade>
              )}
            </>
          ) : isLoading ? (
            <Grid
              templateColumns={'repeat(9, 1fr)'}
              width={850}
              gap={3}
              padding={5}
              alignSelf="start"
            >
              {Array.from({ length: 10 }).map((_, id) => (
                <Fragment key={id}>
                  <GridItem colSpan={4}>
                    <Skeleton height="12px" borderRadius={'full'} />
                  </GridItem>
                  <GridItem colSpan={1}>
                    <Skeleton height="12px" borderRadius={'full'} />
                  </GridItem>
                  <GridItem colSpan={1}>
                    <Skeleton height="12px" borderRadius={'full'} />
                  </GridItem>
                  <GridItem colSpan={2}>
                    <Skeleton height="12px" borderRadius={'full'} />
                  </GridItem>
                  <GridItem colSpan={1}>
                    <Skeleton height="12px" borderRadius={'full'} />
                  </GridItem>
                </Fragment>
              ))}
            </Grid>
          ) : (
            <AggregatedOrderTable
              {...{
                table,
                setRowSelection,
                showColumnManagementModal,
                onColumnManagementModalClose,
                handleColumnManagementSubmit,
                renamingColumnId,
                onRename: renameColumn,
                columnNameMapping: order.columnNameMapping,
                thisOrderId: order.id,
              }}
              orderColumns={order.orderColumns}
            />
          )}
        </PanelContent>
      </Panel>
    </GridItem>
  );
};

export default AggregatedOrderPanel;

// Helper function to get icon for view type
export const getViewTypeIcon = (type: AggregatedOrderViewType | undefined) => {
  switch (type) {
    case AggregatedOrderViewType.Table:
      return faTable;
    case AggregatedOrderViewType.BarChart:
      return faChartColumn;
    case AggregatedOrderViewType.StackedBarChart:
      return faChartColumn;
    case AggregatedOrderViewType.HorizontalBarChart:
      return faChartBar;
    case AggregatedOrderViewType.StackedHorizontalBarChart:
      return faChartBar;
    case AggregatedOrderViewType.LineChart:
      return faChartLine;
    case AggregatedOrderViewType.DoughnutChart:
      return faChartPie;
    default:
      return faTable;
  }
};
